import React, { useState } from "react";
import { Select, MenuItem, FormControl, CircularProgress } from "@mui/material";

const StatusDropdown = ({
  options = [],
  currentValue,
  onChange,
  disabled = false,
  loading = false,
  minWidth = 120,
  // Optional per-caller color overrides, keyed by lowercase status value,
  // e.g. { active: "#128D1F" }. Falls back to the defaults below.
  statusColors = {},
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  // Some callers may accidentally pass an object (e.g. approvalStatus) instead of a string.
  // Normalize anything non-string to a safe string so the UI never crashes.
  const normalizeStatusValue = (value) => {
    if (typeof value === "string") return value;
    if (value && typeof value === "object" && typeof value.status === "string") {
      return value.status;
    }
    return "pending";
  };

  const safeCurrentValue = normalizeStatusValue(currentValue);
  const safeOptions = (Array.isArray(options) ? options : [])
    .map(normalizeStatusValue)
    // de-dupe to avoid repeated menu entries
    .filter((v, i, arr) => arr.indexOf(v) === i);

  const handleChange = async (event) => {
    const newValue = event.target.value;
    if (newValue === safeCurrentValue || disabled || isUpdating) return;

    setIsUpdating(true);
    try {
      await onChange(newValue);
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setIsUpdating(false);
    }
  };
  const getStatusColor = (status) => {
    const normalized = normalizeStatusValue(status).toLowerCase();
    if (statusColors[normalized]) return statusColors[normalized];

    switch (normalized) {
      case "active":
        return "#000000";
      case "inactive":
        return "#000000";
      case "unlisted":
        return "#000000";
      case "approved":
        return "#128D1F";
      case "pending":
        return "#E48900";
      case "denied":
      case "declined":
        return "#BA344C";
      case "removed":
        return "#000000";
      case "automatic":
        return "#1e49c0ff";
      case "public":
        return "#128D1F";
      case "private":
        return "#E48900";
      default:
        return "#000000";
    }
  };

  return (
    <FormControl 
      size="small" 
      sx={{ 
        width: '100%',
        maxWidth: '100%',
        '& .MuiOutlinedInput-root': {
          border: 'none',
          '& fieldset': {
            border: 'none',
          },
          '&:hover fieldset': {
            border: 'none',
          },
          '&.Mui-focused fieldset': {
            border: 'none',
          },
        },
        '& .MuiSelect-select': {
          padding: '4px 8px',
          paddingRight: '24px !important',
          fontFamily: 'Avenir Book, sans-serif',
          fontSize: '14px',
          textTransform: 'capitalize',
          color: getStatusColor(safeCurrentValue),
          backgroundColor: 'transparent',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiSelect-icon': {
          color: '#666',
          right: '4px',
        }
      }}
    >
      <Select
        value={safeCurrentValue || ""}
        onChange={handleChange}
        disabled={disabled || isUpdating || loading}
        displayEmpty
        renderValue={(selected) => {
          const displayValue = normalizeStatusValue(selected);
          if (isUpdating || loading) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={16} />
                <span>{displayValue}</span>
              </div>
            );
          }
          return displayValue || "Select...";
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              border: '1px solid #BABABA',
              borderRadius: '5px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': {
                fontFamily: 'Avenir Book, sans-serif',
                fontSize: '14px',
                textTransform: 'capitalize',
                padding: '8px 16px',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              },
            },
          },
        }}
      >
        {safeOptions.map((option) => (
          <MenuItem 
            key={option} 
            value={option}
            sx={{
              color: getStatusColor(option),
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default StatusDropdown;
